import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { CheckCircle2, AlertCircle, X } from 'lucide-react'

export interface ToastMessage {
  id: string
  type: 'success' | 'error'
  heading?: string
  message: string
}

interface ToastContextValue {
  push: (t: Omit<ToastMessage, 'id'>) => void
  success: (message: string, heading?: string) => void
  error: (message: string, heading?: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  const dismiss = useCallback((id: string) => setToasts((t) => t.filter((x) => x.id !== id)), [])

  const push = useCallback(
    (t: Omit<ToastMessage, 'id'>) => {
      const id = Math.random().toString(36).slice(2)
      setToasts((cur) => [...cur, { ...t, id }])
      setTimeout(() => dismiss(id), t.type === 'error' ? 8000 : 4000)
    },
    [dismiss],
  )

  const value = useMemo<ToastContextValue>(
    () => ({
      push,
      success: (message, heading) => push({ type: 'success', message, ...(heading ? { heading } : {}) }),
      error: (message, heading) => push({ type: 'error', message, ...(heading ? { heading } : {}) }),
    }),
    [push],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="fixed flex flex-col"
        style={{ bottom: 24, right: 24, gap: 8, zIndex: 50 }}
        role="status"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <Toast key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function Toast({ toast, onDismiss }: { toast: ToastMessage; onDismiss: () => void }) {
  const Icon = toast.type === 'success' ? CheckCircle2 : AlertCircle
  const color = toast.type === 'success' ? '#22C55E' : '#F05042'
  return (
    <div
      style={{
        width: 298, backgroundColor: '#1A1D21', border: '0.5px solid #4B5563',
        padding: 10, display: 'flex', alignItems: 'center', gap: 10,
      }}
    >
      <Icon size={16} color={color} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {toast.heading && (
          <p className="font-mulish font-bold text-white" style={{ fontSize: 12, lineHeight: '16px' }}>{toast.heading}</p>
        )}
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <p className="font-mulish font-medium text-white" style={{ fontSize: 12, lineHeight: '16px', flex: 1 }}>
            {toast.message}
          </p>
          <button onClick={onDismiss} aria-label="Dismiss" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}>
            <X size={16} className="text-text-support flex-shrink-0" />
          </button>
        </div>
      </div>
    </div>
  )
}
